import React, { useState, useEffect } from 'react';
import { Navbar } from './components/Navbar';
import { SimpleWizard } from './components/SimpleWizard';
import { SecurityDossier } from './components/SecurityDossier';
import { CvTailor } from './components/CvTailor';
import { ApplicationTracker } from './components/ApplicationTracker';
import { OutreachGenerator } from './components/OutreachGenerator';
import { FastCashPlaybook } from './components/FastCashPlaybook';
import { CvPreviewModal } from './components/CvPreviewModal';
import { CloudflareDeployModal } from './components/CloudflareDeployModal';
import { UploadCvModal } from './components/UploadCvModal';
import { MasterProfile, JobApplication } from './types';
import { storageService } from './services/storageService';

export function App() {
  // Default to Super Simple 3-Step Wizard
  const [activeTab, setActiveTab] = useState<'simple_start' | 'dossier' | 'cv_tailor' | 'tracker' | 'outreach' | 'playbook'>('simple_start');
  
  // Master Profile State (Specialized in Cybersecurity & MSP)
  const [masterProfile, setMasterProfile] = useState<MasterProfile>(() => storageService.getProfile());
  
  // Applications Pipeline State
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [isLoadingApps, setIsLoadingApps] = useState(true);

  // Outreach Pre-fill State
  const [outreachData, setOutreachData] = useState<{
    company: string;
    role: string;
    emailSubject: string;
    emailBody: string;
    inMail: string;
  } | undefined>(undefined);

  // Modals
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [printTailoredSummary, setPrintTailoredSummary] = useState<string | undefined>(undefined);
  const [printTailoredBullets, setPrintTailoredBullets] = useState<string[] | undefined>(undefined);
  const [isDeployModalOpen, setIsDeployModalOpen] = useState(false);
  const [isUploadCvModalOpen, setIsUploadCvModalOpen] = useState(false);

  // Load initial applications from storage / D1
  useEffect(() => {
    storageService.getApplications().then((data) => {
      setApplications(data);
      setIsLoadingApps(false);
    });
  }, []);

  const handleSaveApplication = async (app: JobApplication) => {
    const updated = await storageService.saveApplication(app);
    setApplications(updated);
  };

  const handleDeleteApplication = async (id: string) => {
    const updated = await storageService.deleteApplication(id);
    setApplications(updated);
  };

  const handleOpenOutreachWithData = (data: { company: string; role: string; emailSubject: string; emailBody: string; inMail: string }) => {
    setOutreachData(data);
    setActiveTab('outreach');
  };

  const handleOpenPrintPreview = (summary?: string, bullets?: string[]) => {
    setPrintTailoredSummary(summary);
    setPrintTailoredBullets(bullets);
    setIsPrintModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col selection:bg-emerald-500 selection:text-white">
      
      {/* Top Navigation */}
      <Navbar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onOpenDeployGuide={() => setIsDeployModalOpen(true)}
        onOpenUploadCv={() => setIsUploadCvModalOpen(true)}
      />

      {/* Main Content Area */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8">
        
        {activeTab === 'simple_start' && (
          <SimpleWizard
            masterProfile={masterProfile}
            setMasterProfile={(p) => {
              setMasterProfile(p);
              storageService.saveProfile(p);
            }}
            onSaveToPipeline={handleSaveApplication}
            onOpenPrintPreview={handleOpenPrintPreview}
            onOpenUploadCvModal={() => setIsUploadCvModalOpen(true)}
          />
        )}

        {activeTab === 'dossier' && (
          <SecurityDossier
            masterProfile={masterProfile}
            onOpenPrintDossier={() => window.print()}
          />
        )}

        {activeTab === 'tracker' && (
          <ApplicationTracker
            applications={applications}
            onSaveApplication={handleSaveApplication}
            onDeleteApplication={handleDeleteApplication}
          />
        )}

        {activeTab === 'cv_tailor' && (
          <CvTailor
            masterProfile={masterProfile}
            setMasterProfile={(p) => {
              setMasterProfile(p);
              storageService.saveProfile(p);
            }}
            onSaveToPipeline={handleSaveApplication}
            onOpenOutreachWithData={handleOpenOutreachWithData}
            onOpenPrintPreview={handleOpenPrintPreview}
          />
        )}

        {activeTab === 'outreach' && (
          <OutreachGenerator initialData={outreachData} />
        )}

        {activeTab === 'playbook' && (
          <FastCashPlaybook />
        )}

      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800/80 bg-slate-950 py-6 text-center text-xs text-slate-500 print:hidden">
        <div className="max-w-7xl mx-auto px-4 space-y-1">
          <p>
            CyberHunt AI • Cybersecurity, InfoSec & MSP Contract Dossier & Career Hub
          </p>
          <p className="text-[11px] text-slate-600">
            Hosted 100% Free on Cloudflare Pages, Workers AI & D1 Database
          </p>
        </div>
      </footer>

      {/* ATS CV Printable Modal */}
      <CvPreviewModal
        isOpen={isPrintModalOpen}
        onClose={() => setIsPrintModalOpen(false)}
        masterProfile={masterProfile}
        tailoredSummary={printTailoredSummary}
        tailoredBullets={printTailoredBullets}
      />

      {/* CV Upload Modal */}
      <UploadCvModal
        isOpen={isUploadCvModalOpen}
        onClose={() => setIsUploadCvModalOpen(false)}
        masterProfile={masterProfile}
        onProfileUpdated={(p) => setMasterProfile(p)}
      />

      {/* Cloudflare 2-Minute Deployment Modal */}
      <CloudflareDeployModal
        isOpen={isDeployModalOpen}
        onClose={() => setIsDeployModalOpen(false)}
      />

    </div>
  );
}

export default App;
